import { ActiveInstallationProcessPayload } from "@src/types/ota-update";
import { getInstallationErrorDetails } from "@src/cloud-connect/ota-update/installation-error-details";
import { OTA_OBJECT_TYPE } from "@src/cloud-connect/ota-update/constants";
import { useI18N } from "@src/language/language-context";

interface UseOtaErrorBoxProps {
  activeInstallationData: ActiveInstallationProcessPayload | null;
  onClose: () => void;
}

export const useOtaErrorBox = ({
  activeInstallationData,
  onClose,
}: UseOtaErrorBoxProps) => {
  const i18n = useI18N();

  const isBackupInstallation =
    activeInstallationData?.firmware?.type === OTA_OBJECT_TYPE.FIRMWARE_BACKUP;
  const installationName = activeInstallationData?.firmware?.filename || "";
  const updateDetails = activeInstallationData?.update_details ?? {};

  const errorDetails = getInstallationErrorDetails(updateDetails);

  const operationId = errorDetails?.operationId;

  const errorPayload = errorDetails?.error;

  const titlePrefixTemplate = isBackupInstallation
    ? i18n("modules.otaErrorBox.titlePrefixBackup")
    : i18n("modules.otaErrorBox.titlePrefixFirmware");

  const installationIdText = operationId
    ? i18n("modules.otaErrorBox.installationIdTemplate").replace(
        "{ID}",
        String(operationId),
      )
    : "";

  const errorCode = (errorPayload?.error_code || "").trim();
  const errorTitle = (errorPayload?.title || "").trim();
  const errorDetail = (errorPayload?.detail || "").trim();

  const titlePrefix = titlePrefixTemplate
    .replace("{itemName}", installationName)
    .replace("{installationId}", installationIdText)
    .trim();

  const itemNameMarker = "__ITEM_NAME__";
  const titleTemplateWithMarker = titlePrefixTemplate.replace(
    "{itemName}",
    itemNameMarker,
  );
  const [titlePrefixBeforeRaw, titlePrefixAfterRaw = ""] =
    titleTemplateWithMarker.split(itemNameMarker);

  const titlePrefixBefore = titlePrefixBeforeRaw.replace(
    "{installationId}",
    installationIdText,
  );
  const titlePrefixAfter = titlePrefixAfterRaw.replace(
    "{installationId}",
    installationIdText,
  );

  const errorParts = [errorCode, errorTitle].filter(Boolean);
  const errorSuffix = errorParts.length > 0 ? `: ${errorParts.join(": ")}` : "";

  const texts = {
    title: `${titlePrefix}${errorSuffix}`,
    titleHasItemName: titlePrefixTemplate.includes("{itemName}"),
    titlePrefixBefore,
    titlePrefixAfter: `${titlePrefixAfter}${errorSuffix}`,
    itemName: installationName,
    description: errorDetail,
  };

  return {
    texts,
    handleClose: onClose,
  };
};
